import styled from 'styled-components';
import { Link as ReactLink } from 'react-router-dom';

type Tone = 'green' | 'orange';

const TONE_MAP: Record<Tone, { soft: string; solid: string; border: string }> =
  {
    green: { soft: '#e6f3ec', solid: '#006633', border: '#c7e4d5' },
    orange: { soft: '#fff1e0', solid: '#f38725', border: '#fcd9b0' },
  };

export const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
`;

export const OptionCard = styled(ReactLink)<{ $tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  .icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $tone }) => TONE_MAP[$tone].soft};
    color: ${({ $tone }) => TONE_MAP[$tone].solid};
    transition: background 0.2s ease, color 0.2s ease;
  }

  .content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ $tone }) => TONE_MAP[$tone].solid};
    opacity: 0;
    transform: translateX(-8px);
    transition: opacity 0.2s ease, transform 0.2s ease;
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${({ $tone }) => TONE_MAP[$tone].border};
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
    text-decoration: none;
    color: inherit;
  }

  &:hover .icon {
    background: ${({ $tone }) => TONE_MAP[$tone].solid};
    color: #ffffff;
  }

  &:hover .arrow {
    opacity: 1;
    transform: translateX(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ $tone }) => TONE_MAP[$tone].solid};
    outline-offset: 2px;
  }
`;

export const SectionTitle = styled.h2`
  margin: 24px 0 10px;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const ActionCard = styled.button<{ $tone: Tone }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  text-align: left;
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  color: inherit;
  width: 100%;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  .icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $tone }) => TONE_MAP[$tone].soft};
    color: ${({ $tone }) => TONE_MAP[$tone].solid};
    transition: background 0.2s ease, color 0.2s ease;
  }

  .content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .description {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  .arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ $tone }) => TONE_MAP[$tone].solid};
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: ${({ $tone }) => TONE_MAP[$tone].border};
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  &:hover:not(:disabled) .icon {
    background: ${({ $tone }) => TONE_MAP[$tone].solid};
    color: #ffffff;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.75;
  }

  &:focus-visible {
    outline: 2px solid ${({ $tone }) => TONE_MAP[$tone].solid};
    outline-offset: 2px;
  }
`;
